import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Layout } from "@/components/Layout";
import { CarCard } from "@/components/CarCard";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plane, Hotel, MapPin, Shield, Headphones, CalendarDays, ChevronRight, Star } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 6;

export default function HomePage() {
  const { t } = useLanguage();
  const [allCars, setAllCars] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [pickupDate, setPickupDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [todayPrices, setTodayPrices] = useState({});

  useEffect(() => {
    axios.get(`${API}/cars`).then((res) => setAllCars(res.data)).catch(() => {});
    axios.get(`${API}/pricing/today`).then((res) => setTodayPrices(res.data)).catch(() => {});
  }, []);

  const cars = allCars.slice(0, visibleCount);
  const remaining = allCars.length - visibleCount;

  const serviceIcons = [Plane, Hotel, Headphones, Shield];

  return (
    <Layout>
      <SEO title="Home" description="Premium car rental in Thessaloniki, Greece. Economy to luxury vehicles. Airport delivery, competitive rates from 20/day." />

      {/* ── Hero ── */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1770847816156-a4041d979580?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Premium car on coastal road at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <MapPin className="w-3.5 h-3.5 text-orange-400" />
              <span className="font-dm-sans text-sm text-white/90">Thessaloniki, Greece</span>
            </div>

            <h1 className="font-outfit font-bold text-5xl md:text-7xl text-white tracking-tight leading-[0.9] mb-4">
              {t("hero.title")}
              <br />
              <span className="text-orange-400">{t("hero.titleAccent")}</span>
            </h1>

            <p className="font-dm-sans text-base md:text-lg text-white/70 leading-relaxed mb-8 max-w-lg">
              {t("hero.subtitle")}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/fleet">
                <Button data-testid="hero-browse-btn" className="bg-orange-500 hover:bg-orange-600 text-white font-outfit font-bold rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-orange-500/30 transition-all hover:-translate-y-1">
                  {t("hero.cta")} <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-quote-btn" variant="outline" className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 hover:text-white font-outfit font-medium rounded-full px-8 py-6 text-lg transition-all">
                  {t("hero.ctaSecondary")}
                </Button>
              </Link>
            </div>
          </div>

          {/* Booking Widget */}
          <div data-testid="booking-widget" className="mt-12 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-6 max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block font-outfit text-sm font-medium text-slate-700 mb-1.5">{t("booking.pickupDate")}</label>
                <Popover open={pickupOpen} onOpenChange={setPickupOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="pickup-date-btn" className="w-full flex items-center gap-2 h-12 bg-white border border-slate-200 rounded-lg px-3 text-left font-dm-sans text-sm hover:border-orange-300 transition-colors">
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                      {pickupDate ? format(pickupDate, "dd/MM/yyyy") : <span className="text-slate-400">{t("booking.pickupDate")}</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={pickupDate} onSelect={(d) => { setPickupDate(d); setPickupOpen(false); }} disabled={(date) => date < new Date()} />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="block font-outfit text-sm font-medium text-slate-700 mb-1.5">{t("booking.returnDate")}</label>
                <Popover open={returnOpen} onOpenChange={setReturnOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="return-date-btn" className="w-full flex items-center gap-2 h-12 bg-white border border-slate-200 rounded-lg px-3 text-left font-dm-sans text-sm hover:border-blue-300 transition-colors">
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                      {returnDate ? format(returnDate, "dd/MM/yyyy") : <span className="text-slate-400">{t("booking.returnDate")}</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={returnDate} onSelect={(d) => { setReturnDate(d); setReturnOpen(false); }} disabled={(date) => date < (pickupDate || new Date())} />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="block font-outfit text-sm font-medium text-slate-700 mb-1.5">{t("booking.pickupLocation")}</label>
                <Select>
                  <SelectTrigger data-testid="pickup-location-select" className="h-12 bg-white border-slate-200 rounded-lg font-dm-sans text-sm">
                    <SelectValue placeholder={t("booking.office")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">{t("booking.office")}</SelectItem>
                    <SelectItem value="airport">{t("booking.airport")}</SelectItem>
                    <SelectItem value="hotel">{t("booking.hotel")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Link to="/fleet">
                <Button data-testid="search-cars-btn" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-outfit font-medium rounded-lg shadow-lg hover:shadow-slate-900/25 transition-all">
                  {t("booking.search")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section data-testid="services-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-semibold text-3xl md:text-5xl text-slate-900 tracking-tight mb-4">
              {t("services.title")}
            </h2>
            <p className="font-dm-sans text-base md:text-lg text-slate-500 max-w-2xl mx-auto">
              {t("services.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t("services.items").map((item, i) => {
              const Icon = serviceIcons[i];
              return (
                <div
                  key={i}
                  data-testid={`service-card-${i}`}
                  className="p-8 rounded-3xl bg-slate-50 hover:bg-white transition-all duration-300 border border-transparent hover:border-slate-200 hover:shadow-lg group"
                >
                  <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center mb-5 group-hover:bg-orange-500 transition-colors">
                    <Icon className="w-6 h-6 text-orange-500 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                  <p className="font-dm-sans text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Featured Cars ── */}
      <section data-testid="featured-cars-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <h2 className="font-outfit font-semibold text-3xl md:text-5xl text-slate-900 tracking-tight mb-3">
                {t("fleet.title")}
              </h2>
              <p className="font-dm-sans text-base md:text-lg text-slate-500">
                {t("fleet.subtitle")}
              </p>
            </div>
            <Link to="/fleet">
              <Button variant="outline" className="font-outfit font-medium rounded-full px-6 border-slate-200 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600 transition-all">
                {t("hero.cta")} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {cars.map((car) => (
              <CarCard key={car.id} car={car} effectivePrice={todayPrices[car.id]} />
            ))}
          </div>

          {/* Load More */}
          {remaining > 0 && (
            <div className="flex justify-center mt-10">
              <button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="flex items-center gap-2 px-8 py-3 bg-white border-2 border-slate-200 hover:border-orange-400 hover:bg-orange-50 text-slate-700 hover:text-orange-600 rounded-full font-outfit font-medium text-sm transition-all shadow-sm hover:shadow-md"
              >
                Load More Cars
                <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {remaining} remaining
                </span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section data-testid="why-us-section" className="py-20 md:py-32 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500 rounded-full blur-[200px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-orange-500 rounded-full blur-[150px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-outfit font-semibold text-3xl md:text-5xl tracking-tight mb-6">
                {t("about.whyUs")}
              </h2>
              <div className="space-y-4">
                {t("about.reasons").map((reason, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mt-0.5 shrink-0">
                      <Star className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-dm-sans text-base text-slate-300">{reason}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="inline-block mt-8">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white font-outfit font-medium rounded-full px-8 py-3 shadow-lg hover:shadow-orange-500/25 transition-all hover:-translate-y-0.5">
                  {t("nav.about")} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1759782178739-0c8298937711?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800"
                alt="Premium Porsche Cayenne"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-outfit font-semibold text-3xl md:text-5xl text-slate-900 tracking-tight mb-4">
            {t("hero.title")} <span className="text-orange-500">{t("hero.titleAccent")}</span>
          </h2>
          <p className="font-dm-sans text-base md:text-lg text-slate-500 mb-8 max-w-xl mx-auto">
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/fleet">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-outfit font-bold rounded-full px-10 py-6 text-lg shadow-xl hover:shadow-orange-500/30 transition-all hover:-translate-y-1">
                {t("hero.cta")}
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="font-outfit font-medium rounded-full px-10 py-6 text-lg border-slate-200 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600 transition-all">
                {t("contact.form.send")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
